/**
 * Access-token rotation, and the one place `POST /auth/refresh` is called from.
 *
 * 🔴 WHY THIS EXISTS: `JWT_ACCESS_TTL` IS **15 MINUTES** AND NOTHING RENEWED IT.
 * The route has existed since §10.0 (`auth.controller.ts` `@Post('refresh')`)
 * and `refreshStaffSession` has existed in `auth.ts` beside it, but no code
 * anywhere called either — so every super admin, school admin, teacher and
 * parent was signed out a quarter of an hour after signing in, on a 401 that
 * `isRetryable` correctly refuses to retry. A 401 is not a transient fault; it
 * is a credential that needed rotating, which is a different repair.
 *
 * 🔴 IT IMPORTS NOTHING FROM `client.ts`, AND THAT IS STRUCTURAL RATHER THAN
 * STYLISTIC. `client.ts` calls into this module on a 401, so an import back the
 * other way is a cycle — and the two things this module would want from it (the
 * base URL, `request`) are exactly the two that must not be used here anyway:
 * the rotation call is the one request that must never itself be retried on a
 * 401. So the caller passes the fully-resolved endpoint in, and the POST below
 * is a bare `fetch`.
 *
 * ⚠️ SINGLE-FLIGHT IS A CORRECTNESS REQUIREMENT, NOT AN OPTIMISATION. The server
 * claims the presented `jti` with a `deleteMany` and refuses when the count is
 * zero (`staff-auth.service.ts` §refresh: *"Two concurrent refreshes with the
 * same token both verify the signature; only one deletes a row"*). A dashboard
 * that fires six parallel loads therefore gets **one** rotation and five 401s
 * without the lock below — which would sign the user out at the exact moment
 * this code was supposed to keep them in.
 */

import {
  clearParentSession,
  getParentRefreshToken,
  getParentToken,
  setParentSession,
} from '../stores/parentSession'
import {
  clearStaffSession,
  getStaffRefreshToken,
  getStaffSession,
  getStaffToken,
  setStaffSession,
} from '../stores/staffSession'

/**
 * Which store holds the credential being rotated.
 *
 * The two differ in lifetime rather than in protocol — `staffSession.ts` is
 * module state that dies with the tab, `parentSession.ts` is `localStorage` —
 * and `POST /auth/refresh` serves both: it re-reads the user and re-issues from
 * `user.role`, so a parent's refresh token comes back a parent's access token.
 */
export type SessionTier = 'staff' | 'parent'

/** Exactly what `POST /auth/refresh` returns. */
interface RotatedTokens {
  accessToken: string
  refreshToken: string
}

/**
 * Broadcast after every rotation that changed something.
 *
 * ⚠️ IT EXISTS BECAUSE THE TOKEN LIVES IN TWO PLACES ON PURPOSE. `AppRouter.tsx`
 * keeps a React copy so the router re-renders, and `shared/api/` reads the
 * module store because plain `fetch` helpers are outside React's lifecycle.
 * Rotating only the store would leave the router handing every screen a token
 * that expired fifteen minutes ago, so each call would 401, rotate, and succeed
 * on the replay — correct, and twice the requests forever.
 */
export interface RefreshEvent {
  tier: SessionTier
  /** The rotated access token, or `null` when the session was cleared. */
  accessToken: string | null
}

type Listener = (event: RefreshEvent) => void

const listeners = new Set<Listener>()

/** Subscribe; the returned function unsubscribes. */
export function onSessionRefreshed(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function announce(event: RefreshEvent): void {
  // A copy, so a listener that unsubscribes itself cannot mutate the set
  // mid-iteration; a throw is swallowed because one bad subscriber must not
  // turn a successful rotation into a failed request.
  for (const listener of [...listeners]) {
    try {
      listener(event)
    } catch {
      /* not this module's problem */
    }
  }
}

/**
 * Short on purpose: a rotation sits in front of a request the user is waiting
 * on, so the budget is a fraction of `DEFAULT_TIMEOUT_MS` rather than equal to
 * it. A rotation that has not answered in five seconds is not going to rescue
 * this page load.
 */
const REFRESH_TIMEOUT_MS = 5_000

type PostResult =
  /** The credential rotated; these are the replacements. */
  | { outcome: 'rotated'; tokens: RotatedTokens }
  /** The refresh token itself is dead. Nothing to keep — sign out. */
  | { outcome: 'refused' }
  /** Network, timeout, or a 5xx. The credential may well still be good. */
  | { outcome: 'unreachable' }

async function post(endpoint: string, refreshToken: string): Promise<PostResult> {
  let response: Response
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
      // `client.ts`'s rule, for `client.ts`'s reason: this is a bearer-token
      // API and a kids product must not be embeddable or credentialed
      // cross-site.
      credentials: 'omit',
    })
  } catch {
    return { outcome: 'unreachable' }
  }

  /*
   * 🔴 4xx MEANS THE CREDENTIAL, 5xx MEANS THE SERVER, AND CONFLATING THEM IS
   * HOW A BAD MINUTE OF WIFI SIGNS A CLASSROOM OUT. `refresh` answers 401 for a
   * `jti` already claimed, an expired row, and a deactivated user — all of them
   * permanent. A 502 is none of those.
   */
  if (response.status >= 400 && response.status < 500) return { outcome: 'refused' }
  if (!response.ok) return { outcome: 'unreachable' }

  try {
    const parsed: unknown = await response.json()
    if (typeof parsed !== 'object' || parsed === null) return { outcome: 'refused' }
    const { accessToken, refreshToken: next } = parsed as Partial<RotatedTokens>
    /*
     * ⚠️ BOTH OR NEITHER, `staffSession.ts`'s and `parentSession.ts`'s shared
     * rule — a half-pair is a session that cannot re-authenticate.
     */
    if (typeof accessToken !== 'string' || accessToken.length === 0) return { outcome: 'refused' }
    if (typeof next !== 'string' || next.length === 0) return { outcome: 'refused' }
    return { outcome: 'rotated', tokens: { accessToken, refreshToken: next } }
  } catch {
    /*
     * 🔴 `refused`, NOT `unreachable`, AND THE DIFFERENCE MATTERS. A 2xx we
     * cannot read still means the server rotated: the old `jti` is deleted and
     * the replacement is in a body we just failed to parse. There is no
     * credential left to keep, so treating this as a transient fault would hold
     * a token that is already revoked and 401 on every call until the tab
     * closes. Signing out is the honest end of that.
     */
    return { outcome: 'refused' }
  }
}

/**
 * Which session should be rotated, or `null` when there is nothing to rotate.
 *
 * 🔴 IT REFUSES WHILE IMPERSONATING, AND THIS IS A SECURITY RULE `staffSession`
 * ALREADY WROTE DOWN: *"pairing the impersonated access token with the
 * operator's refresh token would let a `POST /auth/refresh` silently promote the
 * session back without anybody choosing to."* `ImpersonationService` issues no
 * refresh token, so the one in the store still belongs to the operator —
 * rotating it mid-impersonation would swap an operator out of the account they
 * are inspecting and back into their own, with no banner and nobody having asked
 * for it. The grant is meant to die on its own fifteen-minute clock (§8.1); this
 * is what lets it.
 */
function tierToRotate(): SessionTier | null {
  const staff = getStaffSession()
  if (staff) {
    if (staff.impersonatorId !== null) return null
    return getStaffRefreshToken() ? 'staff' : null
  }
  return getParentRefreshToken() ? 'parent' : null
}

async function rotate(endpoint: string): Promise<string | null> {
  const tier = tierToRotate()
  if (!tier) return null

  const refreshToken = tier === 'staff' ? getStaffRefreshToken() : getParentRefreshToken()
  if (!refreshToken) return null

  const result = await post(endpoint, refreshToken)

  // The credential may still be good — leave the session exactly as it was and
  // let the caller surface the original 401. Clearing here would turn a dropped
  // connection into a sign-out.
  if (result.outcome === 'unreachable') return null

  if (result.outcome === 'refused') {
    /*
     * 🔴 A REFUSAL MAY MEAN "ANOTHER TAB GOT THERE FIRST", AND THAT IS NOT A
     * SIGN-OUT.
     *
     * The single-flight lock above serialises rotations within ONE tab; it
     * cannot serialise two. Both stores now persist, so two tabs of the same
     * dashboard share one rotatable credential, and if both read it in the same
     * instant the server answers exactly once: `staff-auth.service.ts` claims
     * the `jti` with a `deleteMany` and returns 401 when the count is zero. The
     * loser is holding a token that was spent a millisecond ago BY ITS OWN
     * USER — indistinguishable, from the status code alone, from one that was
     * revoked. Clearing on that would sign somebody out of both tabs for having
     * two tabs open.
     *
     * ⚠️ SO THE TEST IS WHETHER THE STORE HAS MOVED ON. The stores re-read
     * storage on every call, so a refresh token that is no longer the one we
     * presented is a rotation that landed while ours was in flight — the winner
     * wrote its replacement, and adopting it is both correct and what the caller
     * needs to replay with. If the token is unchanged, the refusal is genuine:
     * the row is gone, the user was deactivated, or somebody signed out.
     */
    const current = tier === 'staff' ? getStaffRefreshToken() : getParentRefreshToken()
    if (current !== null && current !== refreshToken) {
      const superseded = tier === 'staff' ? getStaffToken() : getParentToken()
      if (superseded !== null) {
        // Announced like any rotation: this tab's React copy is holding the
        // access token that just 401'd, and would 401 with it again.
        announce({ tier, accessToken: superseded })
        return superseded
      }
    }

    if (tier === 'staff') clearStaffSession()
    else clearParentSession()
    announce({ tier, accessToken: null })
    return null
  }

  if (tier === 'parent') {
    setParentSession(result.tokens)
  } else if (!setStaffSession(result.tokens)) {
    /*
     * `setStaffSession` returns null for a token whose `role` claim is not one
     * we recognise, and has already stored neither half. Fail closed the way it
     * does: a rotation that produced an undescribable principal is a sign-out.
     */
    clearStaffSession()
    announce({ tier, accessToken: null })
    return null
  }

  announce({ tier, accessToken: result.tokens.accessToken })
  return result.tokens.accessToken
}

/** The in-flight rotation, so concurrent 401s share one call. See the header. */
let inFlight: Promise<string | null> | null = null

/**
 * Rotate the signed-in user's access token.
 *
 * Resolves to the new access token, or `null` when there was nothing to rotate,
 * the rotation failed, or the session was cleared. Never rejects — a caller on
 * the 401 path is already holding a failure and needs an answer, not a second
 * exception to unwind.
 *
 * @param endpoint fully-resolved URL of `POST /auth/refresh`, supplied by
 * `client.ts` so this module needs no knowledge of the base URL or API prefix.
 */
export function refreshUserToken(endpoint: string): Promise<string | null> {
  inFlight ??= rotate(endpoint).finally(() => {
    inFlight = null
  })
  return inFlight
}

/**
 * Test seam. The module-level lock outlives a single test, and a rotation left
 * in flight by one case would be handed to the next.
 */
export function resetRefreshStateForTests(): void {
  inFlight = null
  listeners.clear()
}
